import * as React from 'react';
import S, { DataSignal } from 's-js';
import * as $ from 'jquery';
import * as cx from 'classnames';
import TZDate from 'lib/TZDate';
import SuperCritical, { SuperCriticalComponent, SuperCriticalReference } from 'lib/supercritical';
import { Error as ValidationError } from 'lib/validationWatcher';
import { noDefault } from 'lib/eventWrappers';
import { noPropagation } from 'lib/eventWrappers';

import { LocalizationContext } from 'common/models/LocalizationContext';

import { DateTimeEditPopup, NullableDateTimeEditPopup } from './model';

import { DateTimePicker, NullableDateTimePicker } from 'portcall/datetimepicker/view';
import { PCModal } from 'portcall/pcmodal/view';

interface PopupProps {
    localization : LocalizationContext;
    title? : string;
    min? : () => TZDate;
    max? : () => TZDate;
}

// TH 07/10/2017 -- A popup for editing a TZDate's date and time portions simultaneously.
// See note in ./model for rationale.
// Splitting the popup component from the trigger component so that the popup can
// potentially live inside a different parent element than the trigger. This avoids
// position: fixed issues on the PJO details popup in particular.

export const DateTimeEditPopupView = (props : PopupProps & {
    modelContainer : () => DateTimeEditPopup | null
}) =>
    <PopupView {...props} nullable={false} />;

export const NullableDateTimeEditPopupView = (props : PopupProps & {
    modelContainer : () => NullableDateTimeEditPopup | null
}) =>
    <PopupView {...props} nullable />;

interface InternalPopupViewProps extends PopupProps {
    modelContainer : () => DateTimeEditPopup | NullableDateTimeEditPopup | null;
    nullable : boolean;
}

class PopupView extends SuperCriticalComponent<InternalPopupViewProps> {
    dateInputRef : (e : HTMLInputElement | null) => void;
    validationUpdatesPaused : DataSignal<boolean>;

    constructor(props : InternalPopupViewProps) {
        super(props);
        this.dateInputRef = SuperCriticalReference(this, (props : InternalPopupViewProps, el : HTMLInputElement) => {
            // TH 07/14/2017 -- Same technique as login popup to autofocus date input.
            // See portcall/login/view.tsx.
            const $modal = $(el).closest('.modal'),
                focus = () => {
                    el.focus();
                    el.select();
                };

            $modal.on('shown.bs.modal', focus);
            S.cleanup(() => $modal.off('shown.bs.modal', focus));
        });
        this.validationUpdatesPaused = S.value(false);
        S.root(() => S(() => {
            if (this.smounted() && this.validationUpdatesPaused()) {
                const unpause = () => this.validationUpdatesPaused(false);
                document.addEventListener('mouseup', unpause);
                document.addEventListener('touchend', unpause);
                S.cleanup(() => {
                    document.removeEventListener('mouseup', unpause);
                    document.removeEventListener('touchend', unpause);
                })
            }
        }));
    }

    srender({
        modelContainer,
        nullable,
        title,
        ...rest
    } : InternalPopupViewProps) {
        const m = modelContainer();
        return (
            <PCModal
                className="datetimeedit-popup"
                shown={() => !!m} 
                onBackDropClick={m?.Cancel}
            >
                <div className="modal-content pc-card">
                    <div className="pc-card-header pc-card-title">{title || 'Change Date and Time'}</div>
                    {/* DC 08/15/2017 -- Added form element and moved onClick action to form to allow users to press Enter
                                         to save the datetime changes and avoid an IE redirect to the index page */}
                    <form onSubmit={m ? noDefault(m.Save) : undefined}>
                        <div>
                            {m && (nullable ?
                                <NullableDateTimePicker
                                    value={(m as NullableDateTimeEditPopup).Draft}
                                    inputRef={{ date: this.dateInputRef }}
                                    {...rest}
                                /> :
                                <DateTimePicker
                                    value={(m as DateTimeEditPopup).Draft}
                                    inputRef={{ date: this.dateInputRef }}
                                    {...rest}
                                />
                            )}
                        </div>
                        {m  &&
                            <ValidationErrors
                                errors={m.ValidationErrors}
                                paused={this.validationUpdatesPaused}
                            />
                        }
                        <div className="controls">
                            <a href="" className="cancel-btn" onClick={m ? noDefault(m.Cancel) : undefined}>Cancel</a>
                            <button
                                className="pc-button"
                                // Pausing validation error updates while the submit button is clicked.
                                // This fixes a silly bug that occurs when:
                                //   1) The user types into the Kendo widget, then
                                //   2) Without blurring the Kendo input first, they hit OK, and
                                //   3) The new value causes the height of the rendered validation errors to decrease.
                                // The observed outcome is that the user's OK click seems to get ignored, such that
                                // they have to click OK a 2nd time. The problem is that when the validation errors'
                                // height decreases, the OK button moves up while the user's click is still in progress.
                                // So when the user releases their click, they're no longer over the OK button.
                                // Pausing the validation update fixes the issue.
                                onMouseDown={() => this.validationUpdatesPaused(true)}
                                onTouchStart={() => this.validationUpdatesPaused(true)}
                            >
                                OK
                            </button>
                        </div>
                    </form>
                </div>
            </PCModal>
        );
    }
}

interface ValidationErrorsProps {
    errors : () => ValidationError[];
    paused : () => boolean;
}

class ValidationErrors extends SuperCriticalComponent<ValidationErrorsProps> {
    renderedErrors : DataSignal<React.ReactElement<any>[]>;

    constructor(initProps : ValidationErrorsProps) {
        super(initProps);
        this.renderedErrors = S.value([]);
        S.root(() => S(() => {
            if (this.smounted()) {
                const props = this.sprops();
                if (!props.paused()) {
                    this.renderedErrors(props.errors().map(err =>
                        <ValidationErrorView key={err.Field} error={err} />
                    ));
                }
            }
        }));
    }

    srender() {
        return (
            <div className="validation-errors">{this.renderedErrors()}</div>
        );
    }
}

let ValidationErrorView = SuperCritical(({ error } : { error : ValidationError }) =>
    <div className="validation-err">{error.ErrorText}</div>
);

interface TriggerProps {
    onTriggerPopup : () => void;
    localization : LocalizationContext;
    min? : () => TZDate;
    max? : () => TZDate;
    disabled? : boolean;
    tabindex? : { date? : number, time? : number };
}

// TH 07/10/2017 -- A trigger for opening one of the popups above.
// Presented as a readonly DateTimePicker w/ slightly different styling.

export const DateTimeEditPopupTrigger = (props : TriggerProps & {
    value : DataSignal<TZDate>
}) =>
    <PopupTrigger {...props} nullable={false} />;

export const NullableDateTimeEditPopupTrigger = (props : TriggerProps & {
    value : DataSignal<TZDate | null>
}) =>
    <PopupTrigger {...props} nullable />;

let PopupTrigger = SuperCritical(({ value, nullable, onTriggerPopup, disabled, ...rest } : TriggerProps & {
    value : DataSignal<TZDate | null>,
    nullable : boolean
}) => {
    const onClick = disabled ? undefined : onTriggerPopup;

    return (
        <span
            className={cx('datetimeedit-trigger', { disabled })}
            onClick={onClick}
            onFocus={onClick}
        >
            {nullable ?
                <NullableDateTimePicker
                    value={value}
                    disabled={true}
                    {...rest}
                /> :
                <DateTimePicker
                    value={value as DataSignal<TZDate>}
                    disabled={true}
                    {...rest}
                />
            }
        </span>
    );
});



// WEBPACK FOOTER //
// ./apps/portcall/datetimeeditpopup/view.tsx